import React, { forwardRef, useState } from 'react'
import {
  EndIconContainer,
  FilledInput,
  FilledNumberInput,
  NumberButton,
  OutlinedInput,
  OutlinedNumberInput,
  StartIconContainer,
} from './styles'

import {
  AddIcon,
  EyeIcon,
  EyeSlashIcon,
  Icon,
  MinusIcon,
  SearchIcon,
} from '../Icons'

import { Box, InputBaseProps, Stack } from '@mui/material'

import CardActionsButtonsGroup from 'components/global/CardActions/CardActionsButtonsGroup'

import CardActionButton from 'components/global/CardActions/CardActionButton'

export interface IInputBaseProps extends Omit<InputBaseProps, 'onChange'> {
  setvalue?: (value: number | string) => void
  onChange?: (value: string | number) => void
  starticon?: JSX.Element
  endicon?: JSX.Element
  endiconchange?: number
  clearable?: boolean
  variant?: string
  multiline?: boolean 
}
const StartAdornment = ({ starticon, size }) => {
  if (!starticon) {
    return null
  }
  return (
    <StartIconContainer position="start">
      {React.cloneElement(starticon, {
        size: size === 'small' ? 1 : 2,
      })}
    </StartIconContainer>
  )
}

const EndAdornment = ({
  type,
  size,
  value,
  setvalue,
  onChange,
  endicon,
  endiconchange,
  clearable,
  passwordHidden,
  setPasswordHidden,
  disabled,
}) => {
  const clearIcon = (
    <i className="fa-regular fa-circle-xmark" style={{ fontSize: 12 }} />
  )

  function getEndIcon(type: string) {
    switch (type) {
    case 'password':
      if (passwordHidden) {
        return {
          icon: <EyeSlashIcon />,
          onChange: () => setPasswordHidden((prev) => !prev),
        }
      } else {
        return {
          icon: <EyeIcon />,
          onChange: () => setPasswordHidden((prev) => !prev),
        }
      }
    case 'text':
      if (value && !disabled) {
        return { icon: clearIcon, onChange: () => { setvalue?.(''); onChange?.('') } }
      } else {
        return {
          icon: <Icon icon={undefined} style={undefined} />,
          onChange: () => {},
        }
      }
    case 'search':
      if (clearable && value && !disabled) {
        return { icon: clearIcon, onChange: () => { setvalue?.(''); onChange?.('') } }
      } else {
        return {
          icon: <SearchIcon />,
          onChange: () => {},
        }
      }
    default:
      if (endicon) {
        return { icon: endicon, onChange: endiconchange }
      } else {
        return null
      }
    }
  }

  const endiconAttributes = getEndIcon(type)
  if (!endiconAttributes) {
    return null
  }

  const endIcon = endicon ? endicon : endiconAttributes.icon
  const endIconChange = endiconchange
    ? endiconchange
    : endiconAttributes.onChange
  return (
    <EndIconContainer position="end" passwordhidden={+passwordHidden}>
      {React.cloneElement(endIcon, {
        size: size === 'small' ? 1 : 2,
        onClick: endIconChange,
      })}
    </EndIconContainer>
  )
}

const InputBase = forwardRef(
  (
    {
      type = 'text',
      multiline = false,
      size = 'small',
      variant = 'outlined',
      value,
      setvalue,
      onChange,
      starticon,
      endicon,
      endiconchange,
      clearable,
      disabled,
      ...props
    }: IInputBaseProps,
    ref
  ) => {
    const [passwordHidden, setPasswordHidden] = useState(true)

    const startAdornmentProps = {
      size,
      starticon,
    }

    const endAdornmentProps = {
      type,
      size,
      value,
      setvalue,
      onChange,
      endicon,
      endiconchange,
      clearable,
      passwordHidden,
      setPasswordHidden,
      disabled,
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setvalue?.(newValue)
      onChange?.(newValue)
    }

    if (type === 'number') {
      if (variant === 'filled') {
        return (
          <Box display="flex" alignItems="center">
            <NumberButton
              variant="contained"
              size="small"
              onClick={() => {
                const num = Number(value)
                setvalue?.(isNaN(num) ? 0 : num - 1)
              }}
              disabled={disabled}
            >
              <MinusIcon />
            </NumberButton>
            <FilledNumberInput
              type={type}
              size={size}
              value={value}
              onChange={handleChange}
              disabled={disabled}
              inputRef={ref}
              {...props}
            />
            <NumberButton
              variant="contained"
              size="small"
              onClick={() => {
                const num = Number(value)
                setvalue?.(isNaN(num) ? 0 : num + 1)
              }}
              disabled={disabled}
            >
              <AddIcon />
            </NumberButton>
          </Box>
        )
      }

      return (
        <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
          <OutlinedNumberInput
            type={type}
            size={size}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            inputRef={ref}
            {...props}
          />
          <CardActionsButtonsGroup>
            <CardActionButton
              iconlabel={'minus'}
              onClick={() => {
                const num = Number(value)
                setvalue?.(isNaN(num) ? 0 : num - 1)
              }}
              disabled={disabled}
            />
            <CardActionButton
              iconlabel={'plus'}
              onClick={() => {
                const num = Number(value)
                setvalue?.(isNaN(num) ? 0 : num + 1)
              }}
              disabled={disabled}
            />
          </CardActionsButtonsGroup>
        </Stack>
      )
    }

    if (variant === 'filled') {
      return (
        <FilledInput
          multiline={!!multiline}
          type={type === 'password' && passwordHidden ? 'password' : 'text'}
          size={size}
          value={value}
          onChange={handleChange}
          starticon={starticon}
          endiconchange={endiconchange ? 1 : 0}
          startAdornment={<StartAdornment {...startAdornmentProps} />}
          endAdornment={<EndAdornment {...endAdornmentProps} />}
          disabled={disabled}
          inputRef={ref}
          {...props}
        />
      )
    }

    return (
      <OutlinedInput
        multiline={!!multiline}
        type={type === 'password' && passwordHidden ? 'password' : 'text'}
        size={size}
        value={value}
        onChange={handleChange}
        starticon={starticon}
        endiconchange={endiconchange ? 1 : 0}
        startAdornment={<StartAdornment {...startAdornmentProps} />}
        endAdornment={<EndAdornment {...endAdornmentProps} />}
        disabled={disabled}
        inputRef={ref}
        {...props}
      />
    )
  }
)

export default InputBase
